/* eslint-disable import/no-named-as-default */
import { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';

import { useIsUserLogged, PaymentInfoModalButton } from '@spotahome/ui-library';

import { hasEligibleOffer } from '@spotahome/ui-library/src/tenant/components/utils/offers';

import { EXTERNAL_REDIRECTION_LOGIN_MODAL } from '@spotahome/ui-library/src/tenant/components/utils/constants';

import { ListingShape } from '../../shapes/listing';
import bookNowButtonShape from '../../shapes/bookNowButton';
import {
  useDisplayListingPricing,
  usePricingContext
} from '../../context/PricingContext';
import OptimizeDates from '../OptimizeDates';
import BookNowDatesInput from '../BookNowDatesInput';
import BookNowPermanentContractLabel from '../BookNowPermanentContractLabel';

import { OPEN_BOOKNOW_DATEPICKER_DESKTOP } from '../../channels';

import BookNowButton from '../BookNowButton';

import BookNowSpinner from './BookNowSpinner';
import BookNowTitle from './BookNowTitle';
import BookNowPriceTable from './BookNowPriceTable';

import './BookNowCard.scss';

const IS_AVAILABLE_VALIDATION = 'available';

const BookNowCard = ({
  listing,
  bookingStatus,
  bookingRequestFormUrl,
  hasGAP = false,
  moveIn = '',
  moveOut = '',
  rentalTypes = [],
  onDatesChange = () => {},
  showSpinner,
  deviceType,
  dateFormat,
  showSocialButtons = true
}) => {
  const isUserLogged = useIsUserLogged();
  const { pricingDetails } = usePricingContext();
  const [isCalendarOpen, toggleCalendar] = useState(false);
  const displayPricing = useDisplayListingPricing();

  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'variant';

  const hasPricingDetails = useMemo(
    () => Object.keys(pricingDetails).length > 0,
    [pricingDetails]
  );

  const isBookingAvailable = useMemo(
    () =>
      (!!pricingDetails.downPayment &&
        bookingStatus === IS_AVAILABLE_VALIDATION) ||
      (pricingDetails.pricing &&
        pricingDetails.pricing.contractDurationType === 'permanent' &&
        moveIn),
    [bookingStatus, pricingDetails, moveIn]
  );

  const handleLoginModalOpen = useCallback(() => {
    if (!isUserLogged) {
      Mediator.publish(EXTERNAL_REDIRECTION_LOGIN_MODAL, {
        url: bookingRequestFormUrl,
        initialAction: 'signup',
        renderTitle: action => trans(`${action}.modal.title`),
        renderSubtitle: action => trans(`${action}.modal.subtitle`),
        events: {
          open: {
            eventCategory: 'signup/login modal',
            eventAction: 'Book-now-with-dates',
            eventLabel: 'open'
          },
          close: {
            eventCategory: 'signup/login modal',
            eventAction: 'Book-now-with-dates',
            eventLabel: 'close'
          }
        }
      });
    }
  }, [bookingRequestFormUrl, isUserLogged]);

  const handleBookNowClickNoDates = useCallback(() => {
    toggleCalendar(true);
  }, []);

  useEffect(() => {
    Mediator.subscribe(
      OPEN_BOOKNOW_DATEPICKER_DESKTOP,
      handleBookNowClickNoDates
    );

    return () =>
      Mediator.unsubscribe(
        OPEN_BOOKNOW_DATEPICKER_DESKTOP,
        handleBookNowClickNoDates
      );
  }, [handleBookNowClickNoDates]);

  const handleCalendarClose = () => toggleCalendar(false);

  const hasOffer = hasEligibleOffer(listing.offers ?? [], { moveIn, moveOut });

  const offers = listing.offers;

  const renderButtons = () => (
    <span className="booknow-card__buttons">
      <BookNowButton
        bookingStatus={bookingStatus}
        hasGAP={hasGAP}
        href={bookingRequestFormUrl}
        instantBooking={listing.instantBooking}
        isLogged={isUserLogged}
        maxStay={listing.maxStay}
        minStay={listing.minStay}
        moveIn={moveIn}
        moveOut={moveOut}
        moveInDatesRanges={listing.moveInDatesRanges}
        allowsPermanentContract={listing.allowsPermanentContract}
        onClickBookNowButton={handleBookNowClickNoDates}
        rentableUnitId={listing.id}
        unavailabilities={listing.unavailabilities}
        listing={listing}
        pricing={pricingDetails}
        hasOffer={hasOffer}
        onClickSuccessBookNowButton={handleLoginModalOpen}
      />
    </span>
  );

  return (
    <div className="booknow-card">
      <BookNowTitle
        showPrice={!!moveIn || isListingSimplificationHeaderVariant}
        deviceType={deviceType}
        listingId={listing.id}
        showOffersLabel={hasOffer}
        offers={offers}
        appliedOfferValue={displayPricing?.discount}
        showSocialButtons={showSocialButtons}
      />
      <div className="booknow-card__body">
        {listing.allowsPermanentContract && (
          <BookNowPermanentContractLabel
            allowsPermanentContract={listing.allowsPermanentContract}
            allowsTemporalContract={listing.allowsTemporalContract}
            countryCode={listing.countryCode}
          />
        )}
        <BookNowDatesInput
          availabilities={listing.availabilities}
          maxStay={listing.maxStay}
          minStay={listing.minStay}
          moveInDatesRanges={listing.moveInDatesRanges}
          contractDurationTypesAllowed={listing.contractDurationTypesAllowed}
          moveIn={moveIn}
          moveOut={moveOut}
          rentalTypes={rentalTypes}
          show={isCalendarOpen}
          onChange={onDatesChange}
          onCalendarClose={handleCalendarClose}
          dateFormat={dateFormat}
          cityId={listing.cityId}
        />
        {moveIn && moveOut ? (
          <OptimizeDates
            contractType={listing.contractType}
            moveIn={moveIn}
            moveOut={moveOut}
            onApplyOptimizedDates={onDatesChange}
            listingId={listing.id}
          />
        ) : null}
        {hasPricingDetails && (
          <BookNowPriceTable
            reservationPrice={pricingDetails.reservationPrice}
            bookingFee={pricingDetails.bookingFee}
            cityId={listing.cityId}
          />
        )}
        <BookNowSpinner showSpinner={showSpinner} />
        <div className="booknow-card--button">
          {isBookingAvailable && (
            <div className="booknow-card__booking-summary">
              <PaymentInfoModalButton
                dataTest="booking-summary"
                linkClassName="booknow-card__open-payment-info"
                linkText={trans('listing.book-now-card.payment-summary')}
              />
            </div>
          )}
          {renderButtons()}
        </div>
      </div>
    </div>
  );
};

BookNowCard.propTypes = {
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  rentalTypes: PropTypes.arrayOf(PropTypes.string),
  onDatesChange: PropTypes.func,
  listing: PropTypes.shape(ListingShape).isRequired,
  ...bookNowButtonShape,
  hasGAP: PropTypes.bool,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired,
  dateFormat: PropTypes.string.isRequired,
  showSocialButtons: PropTypes.bool
};

export default BookNowCard;
